// src/redux/services/userApi.js
// ✅ FIXED USER API + Language APIs

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:7600/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem('token');
    if (token) headers.set('x-auth-token', token);
    return headers;
  },
});

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery,
  tagTypes: ['Users', 'Profile', 'Language','QuickLinks'],
  endpoints: (builder) => ({

    // ✅ Get Logged-in User Profile
    getUserProfile: builder.query({
      query: () => '/users/profile',
      providesTags: ['Profile'],
      transformResponse: (response) => response?.user || response?.data || response
    }),

    // ✅ Update Profile
    updateUserProfile: builder.mutation({
      query: (profileData) => ({
        url: '/users/profile',
        method: 'PUT',
        body: profileData,
      }),
      invalidatesTags: ['Profile'],
    }),

    changeMyPassword: builder.mutation({
      query: (body) => ({
        url: '/users/change-password',
        method: 'PUT',
        body,
      }),
    }),

    // ✅ Upload Profile Picture
    uploadProfilePicture: builder.mutation({
      query: (formData) => ({
        url: '/users/profile/picture',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Profile'],
    }),

    // ✅ Get users by role
    getUsersByRole: builder.query({
      query: (role) => `/users/by-role/${role}`,
      transformResponse: (response) => response?.data || response || [],
    }),

    // =========================
    // ✅ Language APIs (India)
    // =========================

    // list languages (no auth needed usually)
    getIndiaLanguages: builder.query({
      query: () => '/language/languages',
      providesTags: ['Language'],
      transformResponse: (response) => response?.languages || [],
    }),

    // get my saved language (auth)
    getMyLanguage: builder.query({
      query: () => '/settings/language',
      providesTags: ['Language'],
      transformResponse: (response) => response?.language || 'en',
    }),

    getLanguageStrings: builder.query({
  query: (lang) => `/language/strings?lang=${lang}`,
  providesTags: ['Language'],
  transformResponse: (response) => response?.strings || {},
}),

    // update my language (auth)
    updateMyLanguage: builder.mutation({
      query: (language) => ({
        url: '/settings/language',
        method: 'PUT',
        body: { language }, // expects code like "hi"
      }),
      invalidatesTags: ['Language', 'Profile'],
    }),

    getMyQuickLinks: builder.query({
  query: (q = "") => `/quicklinks${q ? `?q=${encodeURIComponent(q)}` : ""}`,
  providesTags: ["QuickLinks"],
  transformResponse: (res) => res?.data || [],
}),

createQuickLink: builder.mutation({
  query: (body) => ({
    url: "/quicklinks",
    method: "POST",
    body,
  }),
  invalidatesTags: ["QuickLinks"],
}),

updateQuickLink: builder.mutation({
  query: ({ id, ...body }) => ({
    url: `/quicklinks/${id}`,
    method: "PUT",
    body,
  }),
  invalidatesTags: ["QuickLinks"],
}),

deleteQuickLink: builder.mutation({
  query: (id) => ({
    url: `/quicklinks/${id}`,
    method: "DELETE",
  }),
  invalidatesTags: ["QuickLinks"],
}),

uploadQuickLinkLogo: builder.mutation({
  query: ({ id, formData }) => ({
    url: `/quicklinks/${id}/logo`,
    method: "POST",
    body: formData, // multipart
  }),
  invalidatesTags: ["QuickLinks"],
}),

  }),
});

export const {
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useChangeMyPasswordMutation,
  useUploadProfilePictureMutation,
  useGetUsersByRoleQuery,

  // ✅ language hooks
  useGetIndiaLanguagesQuery,
  useGetMyLanguageQuery,
  useUpdateMyLanguageMutation,
  useGetLanguageStringsQuery,
   useGetMyQuickLinksQuery,
  useCreateQuickLinkMutation,
  useUpdateQuickLinkMutation,
  useDeleteQuickLinkMutation,
  useUploadQuickLinkLogoMutation,
} = userApi;
